import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Info, CheckCircle2 } from "lucide-react";

const FALLBACK_TERM = `Ao aprovar este orçamento, o(a) diretor(a) e responsável pela escola declara ciência e concordância com as condições abaixo:

1. Os valores em HONORÁRIOS incluem material e mão de obra, bem como os impostos aproximados já embutidos no valor total.
2. A forma de pagamento indicada será respeitada após a entrega da nota fiscal.
3. O prazo de entrega é estimado em dias úteis e pode variar conforme condições do tempo, entrega de materiais e disponibilidade da equipe técnica.
4. A garantia cobre defeitos relacionados aos serviços executados dentro do período informado — não cobre uso inadequado, vandalismo ou intervenções de terceiros.
5. Após a aprovação, qualquer alteração ou inclusão de item deverá ser formalizada por novo orçamento ou aditivo.
6. A validade deste orçamento é limitada à data indicada; após este prazo, valores e condições podem ser revistos.`;

/**
 * Dialog that shows the acknowledgment term.
 * When `mustAccept` is true, requires the user to tick a checkbox then click accept.
 * Calls `onAccept` on confirmation.
 * When `accepted` is true, shows an "already accepted" badge with info.
 */
export default function TermsOfBudgetDialog({
  open,
  onOpenChange,
  text,
  mustAccept = false,
  onAccept,
  accepted = false,
  acceptedAt = "",
  acceptedBy = "",
  dismissable = true,
}) {
  const content = (text && text.trim()) || FALLBACK_TERM;
  const [checked, setChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  const handleAccept = async () => {
    if (!checked) return;
    setConfirming(true);
    try {
      await onAccept?.();
    } finally {
      setConfirming(false);
    }
  };

  // Lock backdrop if must accept and not already accepted
  const canClose = dismissable || accepted;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !canClose) return; onOpenChange(o); }}>
      <DialogContent className="max-w-xl" data-testid="terms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a]">
            <Info className="w-5 h-5 text-blue-600" />
            Termo de ciência das condições do orçamento
          </DialogTitle>
        </DialogHeader>

        {accepted && (
          <div
            className="flex items-start gap-2 bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-xs"
            data-testid="terms-already-accepted"
          >
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-green-600" />
            <div>
              <p className="font-semibold">Termo aceito</p>
              {acceptedBy && <p>Por {acceptedBy}</p>}
              {acceptedAt && (
                <p>Em {new Date(acceptedAt).toLocaleString("pt-BR")}</p>
              )}
            </div>
          </div>
        )}

        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line max-h-[55vh] overflow-y-auto">
          {content}
        </div>

        {mustAccept && !accepted && (
          <label
            className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer"
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => setChecked(!!v)}
              data-testid="terms-accept-check"
              className="mt-0.5"
            />
            <span className="text-sm text-amber-900">
              Li, concordo e aceito os termos acima
            </span>
          </label>
        )}

        <DialogFooter className="gap-2">
          {mustAccept && !accepted ? (
            <>
              {dismissable && (
                <Button variant="outline" onClick={() => onOpenChange(false)}>Depois</Button>
              )}
              <Button
                onClick={handleAccept}
                disabled={!checked || confirming}
                className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                data-testid="terms-accept-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {confirming ? "Enviando..." : "Aceitar"}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="terms-close">
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
